/* eslint-disable */
import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Route, Navigate, Routes, useLocation } from 'react-router-dom';
import Loading from './components/Loading';

// Loading fallback component for code splitting
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <Loading size="lg" variant="spinner" text="Loading page..." />
  </div>
);

// Composant wrapper pour gérer la redirection avec l'URL d'origine
const RequireAuth = ({ isAuthenticated, isLoading, children }) => {
  const location = useLocation();

  if (isLoading) {
    return <PageLoader />;
  }

  if (!isAuthenticated) {
    // Sauvegarder l'URL d'origine dans le state de la navigation
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// Valide qu'une URL de redirection est sûre (pas d'open redirect ou XSS)
const isValidRedirectPath = (path) => {
  if (!path || typeof path !== 'string') return false;
  
  // Doit commencer par / (chemin relatif au domaine)
  if (!path.startsWith('/')) return false;
  
  // Bloquer les protocol-relative URLs (//evil.com)
  if (path.startsWith('//')) return false;
  
  // Bloquer les schemes dangereux (javascript:, data:, vbscript:, etc.)
  const dangerousSchemes = /^[a-zA-Z][a-zA-Z0-9+.-]*:/i;
  if (dangerousSchemes.test(path)) return false;
  
  // Bloquer les tentatives d'encodage (%2f%2f = //)
  try {
    const decoded = decodeURIComponent(path);
    if (decoded.startsWith('//') || dangerousSchemes.test(decoded)) return false;
  } catch (e) {
    // Si le décodage échoue, on rejette par précaution
    return false;
  }
  
  return true;
};

// Composant wrapper pour la page de login avec redirection automatique si déjà authentifié
const LoginRoute = ({ isAuthenticated, children }) => {
  const location = useLocation();
  
  if (isAuthenticated) {
    // Récupérer l'URL d'origine depuis le state de navigation
    const from = location.state?.from?.pathname || '/';
    const search = location.state?.from?.search || '';
    
    // Valider le chemin de redirection pour éviter Open Redirect et XSS
    const safePath = isValidRedirectPath(from) ? from : '/';
    const safeSearch = search.startsWith('?') || search === '' ? search : '';
    
    return <Navigate to={safePath + safeSearch} replace />;
  }

  return children;
};

// Core imports (loaded immediately)
import Layout from './Layout';
import Login from './Login';
import NotFound from './NotFound';
import { authenticatedFetch } from './utils/api';
import { NotificationProvider } from './contexts/NotificationContext';
import { TeamProvider } from './contexts/TeamContext';
import AdminRoute from './components/AdminRoute';

// Lazy-loaded page components (code splitting)
const Dashboard = lazy(() => import('./Dashboard'));
const Runners = lazy(() => import('./Runners'));
const Results = lazy(() => import('./Results'));
const Analytics = lazy(() => import('./Analytics'));
const WebhooksConfig = lazy(() => import('./WebhooksConfig'));
const TokensConfig = lazy(() => import('./TokensConfig'));
const Profile = lazy(() => import('./Profile'));
const Billing = lazy(() => import('./Billing'));
const Integrations = lazy(() => import('./Integrations'));
const NotificationsConfig = lazy(() => import('./NotificationsConfig'));
const RiskManagements = lazy(() => import('./RiskManagements'));
const HiddenRepositories = lazy(() => import('./HiddenRepositories'));
const BlacklistedExtensions = lazy(() => import('./BlacklistedExtensions'));
const PersonalAccessTokens = lazy(() => import('./PersonalAccessToken'));
const Teams = lazy(() => import('./Teams'));
const AllNotifications = lazy(() => import('./AllNotifications'));
const NotificationWorkflows = lazy(() => import('./NotificationWorkflows'));
const DeepScans = lazy(() => import('./DeepScans'));
const DeepScanDetail = lazy(() => import('./DeepScanDetail'));

// Admin pages (lazy loaded)
const UserManagement = lazy(() => import('./admin/UserManagement'));
const ProductUpdatesManagement = lazy(() => import('./admin/ProductUpdatesManagement'));
const PatternManagement = lazy(() => import('./admin/PatternManagement'));
const FalsePositiveReports = lazy(() => import('./admin/FalsePositiveReports'));
const LLMConfiguration = lazy(() => import('./admin/LLMConfiguration'));
const QuotaManagement = lazy(() => import('./admin/QuotaManagement'));

function App() {
  const [authState, setAuthState] = useState({
    isAuthenticated: false,
    token: null,
    isLoading: true
  });

  useEffect(() => {
    const verifyToken = async () => {
      const storedToken = localStorage.getItem('token');
      if (!storedToken) {
        setAuthState(prev => ({ ...prev, isLoading: false }));
        return;
      }

      try {
        const response = await authenticatedFetch('/verify-token');
        if (response && response.valid) {
          setAuthState({
            isAuthenticated: true,
            token: storedToken,
            isLoading: false
          });
        } else {
          throw new Error('Token invalid');
        }
      } catch (error) {
        console.error('Erreur lors de la vérification du token:', error);
        handleLogout();
      }
    };
  
    verifyToken();
  }, []);

  const handleLogout = () => {
    setAuthState({
      isAuthenticated: false,
      token: null,
      isLoading: false,
      username: null
    });
    localStorage.removeItem('token');
  };

  const handleLogin = (userData) => {
    setAuthState({
      isAuthenticated: true,
      token: userData.token,
      isLoading: false,
      username: userData.username
    });
    
    localStorage.setItem('token', userData.token);
  };

  if (authState.isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loading size="lg" variant="spinner" text="Loading..." />
      </div>
    );
  }

  return (
    <NotificationProvider>
      <TeamProvider>
        <Router>
          <div className="App">
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/login" element={
                  <LoginRoute isAuthenticated={authState.isAuthenticated}>
                    <Login onLogin={handleLogin} />
                  </LoginRoute>
                } />
                <Route path="/" element={
                  <RequireAuth isAuthenticated={authState.isAuthenticated} isLoading={authState.isLoading}>
                    <Layout onLogout={handleLogout}>
                      <Dashboard />
                    </Layout>
                  </RequireAuth>
                } />
              <Route path="/runners" element={
                <RequireAuth isAuthenticated={authState.isAuthenticated} isLoading={authState.isLoading}>
                  <Layout onLogout={handleLogout}>
                    <Runners />
                  </Layout>
                </RequireAuth>
              } />

              <Route path="/results" element={
                <RequireAuth isAuthenticated={authState.isAuthenticated} isLoading={authState.isLoading}>
                  <Layout onLogout={handleLogout}>
                    <Results />
                  </Layout>
                </RequireAuth>
              } />

              <Route path="/action-items" element={
                <RequireAuth isAuthenticated={authState.isAuthenticated} isLoading={authState.isLoading}>
                  <Layout onLogout={handleLogout}>
                    <RiskManagements />
                  </Layout>
                </RequireAuth>
              } />

              <Route path="/results/:runnerId" element={
                <RequireAuth isAuthenticated={authState.isAuthenticated} isLoading={authState.isLoading}>
                  <Layout onLogout={handleLogout}>
                    <Results />
                  </Layout>
                </RequireAuth>
              } />
              <Route path="/analytics" element={
                <RequireAuth isAuthenticated={authState.isAuthenticated} isLoading={authState.isLoading}>
                  <Layout onLogout={handleLogout}>
                    <Analytics />
                  </Layout>
                </RequireAuth>
              } />
              <Route path="/deepscan" element={
                <RequireAuth isAuthenticated={authState.isAuthenticated} isLoading={authState.isLoading}>
                  <Layout onLogout={handleLogout}>
                    <DeepScans />
                  </Layout>
                </RequireAuth>
              } />
              <Route path="/deepscan/:scanId" element={
                <RequireAuth isAuthenticated={authState.isAuthenticated} isLoading={authState.isLoading}>
                  <Layout onLogout={handleLogout}>
                    <DeepScanDetail />
                  </Layout>
                </RequireAuth>
              } />
              <Route path="/notifications" element={
                <RequireAuth isAuthenticated={authState.isAuthenticated} isLoading={authState.isLoading}>
                  <Layout onLogout={handleLogout}>
                    <AllNotifications />
                  </Layout>
                </RequireAuth>
              } />
              <Route path="/settings" element={
                <RequireAuth isAuthenticated={authState.isAuthenticated} isLoading={authState.isLoading}>
                  <Layout onLogout={handleLogout}>
                    <Navigate to="/settings/webhooks" />
                  </Layout>
                </RequireAuth>
              } />

              <Route path="/settings/webhooks" element={
                <RequireAuth isAuthenticated={authState.isAuthenticated} isLoading={authState.isLoading}>
                  <Layout onLogout={handleLogout}>
                    <WebhooksConfig />
                  </Layout>
                </RequireAuth>
              } />

              <Route path="/settings/tokens" element={
                <RequireAuth isAuthenticated={authState.isAuthenticated} isLoading={authState.isLoading}>
                  <Layout onLogout={handleLogout}>
                    <TokensConfig />
                  </Layout>
                </RequireAuth>
              } />
              <Route path="/settings/hidden-repositories" element={
                <RequireAuth isAuthenticated={authState.isAuthenticated} isLoading={authState.isLoading}>
                  <Layout onLogout={handleLogout}>
                    <HiddenRepositories />
                  </Layout>
                </RequireAuth>
              } />
              <Route path="/settings/ignored-extensions" element={
                <RequireAuth isAuthenticated={authState.isAuthenticated} isLoading={authState.isLoading}>
                  <Layout onLogout={handleLogout}>
                    <BlacklistedExtensions />
                  </Layout>
                </RequireAuth>
              } />
              <Route path="/profile" element={
                <RequireAuth isAuthenticated={authState.isAuthenticated} isLoading={authState.isLoading}>
                  <Layout onLogout={handleLogout}>
                    <Profile />
                  </Layout>
                </RequireAuth>
              } />
              <Route path="/billing" element={
                <RequireAuth isAuthenticated={authState.isAuthenticated} isLoading={authState.isLoading}>
                  <Layout onLogout={handleLogout}>
                    <Billing />
                  </Layout>
                </RequireAuth>
              } />
              <Route path="/settings/integrations" element={
                <RequireAuth isAuthenticated={authState.isAuthenticated} isLoading={authState.isLoading}>
                  <Layout onLogout={handleLogout}>
                    <Integrations />
                  </Layout>
                </RequireAuth>
              } />
              <Route path="/settings/notification-workflows" element={
                <RequireAuth isAuthenticated={authState.isAuthenticated} isLoading={authState.isLoading}>
                  <Layout onLogout={handleLogout}>
                    <NotificationWorkflows />
                  </Layout>
                </RequireAuth>
              } />
              <Route path="/settings/notification-workflows/:workflowId" element={
                <RequireAuth isAuthenticated={authState.isAuthenticated} isLoading={authState.isLoading}>
                  <Layout onLogout={handleLogout}>
                    <NotificationWorkflows />
                  </Layout>
                </RequireAuth>
              } />
              <Route path="/settings/notifications" element={
                <RequireAuth isAuthenticated={authState.isAuthenticated} isLoading={authState.isLoading}>
                  <Layout onLogout={handleLogout}>
                    <NotificationsConfig />
                  </Layout>
                </RequireAuth>
              } />
              <Route path="/settings/personal-tokens" element={
                <RequireAuth isAuthenticated={authState.isAuthenticated} isLoading={authState.isLoading}>
                  <Layout onLogout={handleLogout}>
                    <PersonalAccessTokens />
                  </Layout>
                </RequireAuth>
              } />
              <Route path="/admin/users" element={
                <RequireAuth isAuthenticated={authState.isAuthenticated} isLoading={authState.isLoading}>
                  <Layout onLogout={handleLogout}>
                    <AdminRoute>
                      <UserManagement />
                    </AdminRoute>
                  </Layout>
                </RequireAuth>
              } />
              <Route path="/admin/product-updates" element={
                <RequireAuth isAuthenticated={authState.isAuthenticated} isLoading={authState.isLoading}>
                  <Layout onLogout={handleLogout}>
                    <AdminRoute>
                      <ProductUpdatesManagement />
                    </AdminRoute>
                  </Layout>
                </RequireAuth>
              } />
              <Route path="/teams" element={
                <RequireAuth isAuthenticated={authState.isAuthenticated} isLoading={authState.isLoading}>
                  <Layout onLogout={handleLogout}>
                    <Teams />
                  </Layout>
                </RequireAuth>
              } />
              <Route path="/admin/patterns" element={
                <RequireAuth isAuthenticated={authState.isAuthenticated} isLoading={authState.isLoading}>
                  <Layout onLogout={handleLogout}>
                    <AdminRoute>
                      <PatternManagement />
                    </AdminRoute>
                  </Layout>
                </RequireAuth>
              } />
              <Route path="/admin/false-positive-reports" element={
                <RequireAuth isAuthenticated={authState.isAuthenticated} isLoading={authState.isLoading}>
                  <Layout onLogout={handleLogout}>
                    <AdminRoute>
                      <FalsePositiveReports />
                    </AdminRoute>
                  </Layout>
                </RequireAuth>
              } />
              <Route path="/admin/llm-config" element={
                <RequireAuth isAuthenticated={authState.isAuthenticated} isLoading={authState.isLoading}>
                  <Layout onLogout={handleLogout}>
                    <AdminRoute>
                      <LLMConfiguration />
                    </AdminRoute>
                  </Layout>
                </RequireAuth>
              } />
              <Route path="/admin/quotas" element={
                <RequireAuth isAuthenticated={authState.isAuthenticated} isLoading={authState.isLoading}>
                  <Layout onLogout={handleLogout}>
                    <AdminRoute>
                      <QuotaManagement />
                    </AdminRoute>
                  </Layout>
                </RequireAuth>
              } />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </div>
        </Router>
      </TeamProvider>
    </NotificationProvider>
  );
}

export default App;